/**
 * The catalog under management (handoff §7).
 *
 * The prototype's catalog is one component serving six contexts; this module models **one**
 * of them, managing the host's own list. The other five carry header, hero, pagination and
 * bars that do not belong in this tab, which the prototype itself switches off when embedded.
 */

/** Background of the piece frame. Comes from data, not from a colour heuristic. */
export type PieceTone = "" | "sand" | "sage" | "deep";

/** One catalog piece, as the catalog service describes it. */
export type CatalogPiece = {
  /** Backend `Product.id`, a UUID; the panel never invents a numeric alias for it. */
  id: string;
  name: string;
  /**
   * The ERP's stock code, alphanumeric and searchable (filters handoff §9). Empty, by name, where
   * the backend does not send one — never guessed. Optional so a piece built without it (a
   * fixture, a source that has none) reads as "no code" instead of being a type error.
   */
  sku?: string;
  collection: string;
  /** **Unit** price in integer cents. What the screen shows is the pack total. */
  priceCents: number;
  /**
   * How many pieces go in each pack — **the pack the host chose**, and nothing else.
   *
   * `1` means no pack. It is never the size of a kit: a kit is what the product *is*, a pack is
   * what the host *wants*, and the two are orthogonal (`architecture-standards.md` § "Kit e
   * pacote são coisas diferentes"). Conflating them priced a kit at its own price times its
   * component count.
   */
  pack: number;
  /** Whether the product itself is a set, as the ERP says (`Product.isKit`). */
  isKit: boolean;
  /**
   * How many component **products** the kit holds; null when it is not a kit.
   *
   * Not a count of pieces: each component carries its own quantity, so a kit of two components
   * with three each holds six. Good enough to say "conjunto de N peças" as the design asks, and
   * never used as a pack size or a price multiplier.
   */
  componentCount: number | null;
  /** The unit word the data uses. */
  unit: string;
  /** ERP category, not yet canonicalized. */
  category: string;
  /** Type within the category. */
  type: string;
  material: string;
  tone: PieceTone;
  /** Key in the catalog bucket; the front assembles the address. */
  imageKey: string | null;
  description: string;
};

/** The piece as the host's list sees it: in or out, and with which arrangement. */
export type ListedPiece = CatalogPiece & {
  inList: boolean;
  featured: boolean;
  /** Desired number of physical pieces. */
  total: number;
  /** How many have already been gifted. */
  purchased: number;
};

/**
 * The tab's two scopes: `dentro` is the host's list, `fora` is the whole catalog.
 */
export type CatalogScope = "dentro" | "fora";

/** The "O que exibir" pills, visible only in add scope. */
export type AddFilter = "todos" | "add" | "nao";

/**
 * A pack's total value, the only price the screen presents (canon §10).
 *
 * Correct **because `pack` means only the pack**: a kit carries its own price, already the sum
 * of its components, and multiplying that by anything is how a R$ 900 set came out at R$ 3.600.
 */
export function packTotalCents(piece: Pick<CatalogPiece, "priceCents" | "pack">): number {
  return piece.priceCents * piece.pack;
}

/**
 * How much of a gift one piece is, in the brand's words.
 *
 * The four combinations of kit × pack, and the wording is the **checkout's** (`mappers/cart.ts`
 * `packLabel`), which has modelled the two as independent flags since it was written. This
 * module used to word it from `pack` alone, so a kit only read as a set because the kit's
 * component count had been poured into `pack` — see `architecture-standards.md` § "Kit e pacote
 * são coisas diferentes".
 *
 * `unit` still carries the piece's own word ("peça", "kit") for a single, un-packed item, which
 * is what the panel has always shown there.
 */
export function packLabel(
  piece: Pick<CatalogPiece, "pack" | "unit" | "isKit" | "componentCount">,
): string {
  if (piece.pack > 1) return `conjunto de ${piece.pack} ${piece.isKit ? "kits" : "peças"}`;
  if (piece.isKit && piece.componentCount !== null && piece.componentCount > 1) {
    return `conjunto de ${piece.componentCount} peças`;
  }

  return `1 ${piece.unit}`;
}

/**
 * An order line's label, as the prototype writes it (v2 painel logic 1451-1471): several sets
 * are "2 conjuntos de 6 peças", one set is the piece's own label. Loose pieces are counted as
 * pieces ("3 peças"): the prototype's "3 conjuntos de 1 peça" never occurs in its own data and
 * reads wrong, so it is not reproduced.
 */
export function orderLinePackLabel(
  quantity: number,
  piece: Pick<CatalogPiece, "pack" | "unit" | "isKit" | "componentCount">,
): string {
  if (quantity <= 1) return packLabel(piece);

  return piece.pack > 1 ? `${quantity} conjuntos de ${piece.pack} peças` : `${quantity} peças`;
}

/**
 * What links the panel's pieces to their own page.
 *
 * `slug` empty means the list has no public address to build one from — a list the panel could
 * not name — and every piece falls back to the prototype's own route, `/produtos/[id]`.
 */
export type PieceLinks = {
  slug: string;
  /** Keyed by `Product.id`, the only key the panel has for a piece. */
  listItemIdByProductId: Readonly<Record<string, string>>;
};

/** One option of a facet the server counts for us (filters spec §5). */
export type CatalogFacetOption = {
  /** What the request sends back: a collection's id, or an attribute's value. */
  value: string;
  label: string;
  count: number;
};

/**
 * The facets the catalog answers with, each over its own scope (spec §9, "Resposta").
 *
 * Only what this catalog actually carries: the navigation trail waits for the canonical
 * taxonomy the spec asks the backend for (§9, D-03 and D-04).
 */
export type CatalogFacets = {
  /**
   * The ERP's categories with their live counts — the navigation trail's second level (handoff
   * §4). `value` is the id the request filters by (`categoryId`), `label` the name as the ERP
   * writes it (upper case; presentation is the rail's business, the value stays untouched).
   *
   * **Absent from a response that is already scoped to a category** (measured 2026-10-14: the
   * backend drops `categories` and `groups` once `categoryId` is sent), so the section keeps the
   * last complete list it saw rather than reading this off every page.
   */
  categories: readonly CatalogFacetOption[];
  collections: readonly CatalogFacetOption[];
  types: readonly CatalogFacetOption[];
  materials: readonly CatalogFacetOption[];
  /** The whole catalog's bounds, in integer cents. `null` when it has no price to speak of. */
  price: { minCents: number; maxCents: number } | null;
};

/** One page of the catalog, as the add scope reads it. */
export type CatalogPage = {
  items: readonly ListedPiece[];
  facets: CatalogFacets;
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
};
